// Unified tag filter functionality - works with .tag-filter-item elements
// Supports data-tag with comma/space separated values

/**
 * TagFilter class - handles filtering of items by tags
 * Works with .tag-filter-item elements that have data-tag attribute
 * Supports multiple comma-separated or space-separated values in both filter and item tags
 */
class TagFilter {
	constructor(container) {
		if (!container || !(container instanceof HTMLElement)) {
			throw new Error("TagFilter requires a valid container element");
		}

		this.container = container;
		// Use the unified .tag-filter-item selector
		this.items = Array.from(container.querySelectorAll(".tag-filter-item"));

		// Find select element
		this.selectElement = container.querySelector(".custom-select");

		// Cache items container for accessibility (find first container with items)
		this.itemsContainer = container.querySelector(
			".article-cards, .office-list__items, .employee-list",
		);

		// Cache location name from heading (for office lists)
		const headingElement = container.querySelector(".office-list__heading h2");
		this.locationName = headingElement?.textContent?.trim() || "";

		this.activeFilter = "all";
		this.activeFilterTags = [];
		this.visibleCount = this.items.length;

		// Cache item tags/services for better performance
		this.itemTagsCache = new Map();
		// Regex for tag splitting (compile once, reuse)
		this.tagSplitRegex = /[,\s]+/;

		// Store bound event handlers for cleanup
		this.boundHandlers = {
			selectChange: (e) => this.handleSelectChange(e),
			searchChanged: () => {
				requestAnimationFrame(() => {
					this.performFilter();
				});
			},
		};

		// Initialize
		this.init();
	}

	/**
	 * Initialize the tag filter
	 */
	init() {
		if (this.items.length === 0) return;

		// Set up select element
		if (this.selectElement) {
			this.setupSelectElement();
		}

		// Add ARIA attributes to container
		this.container.setAttribute("role", "region");
		this.container.setAttribute("aria-label", "Filtered content");

		// Create live region for screen reader announcements
		this.createLiveRegion();

		// Create visible "no results" message element (optional, can be added via data attribute)
		if (this.container.dataset.showNoResults === "true") {
			this.createNoResultsMessage();
		}

		// Initialize all items with proper attributes and cache tags
		this.initializeItems();

		// Listen for search filter changes (from filter-search.js)
		this.container.addEventListener(
			"tagFilter:searchChanged",
			this.boundHandlers.searchChanged,
		);
	}

	/**
	 * Cleanup method to remove event listeners and clear cache
	 * Call this when the filter is no longer needed (e.g., dynamic content removal)
	 */
	destroy() {
		// Remove event listeners
		if (this.selectElement) {
			this.selectElement.removeEventListener(
				"change",
				this.boundHandlers.selectChange,
			);
		}
		this.container.removeEventListener(
			"tagFilter:searchChanged",
			this.boundHandlers.searchChanged,
		);

		// Clear cache to prevent memory leaks
		this.itemTagsCache.clear();

		// Clear references
		this.items = [];
		this.container = null;
		this.selectElement = null;
		this.itemsContainer = null;
		this.liveRegion = null;
		this.noResultsMessage = null;
	}

	/**
	 * Set up select element with event listeners
	 */
	setupSelectElement() {
		// Set up accessibility attributes
		if (this.itemsContainer?.id) {
			this.selectElement.setAttribute("aria-controls", this.itemsContainer.id);
		}

		// Set appropriate aria-label if not already set
		if (!this.selectElement.hasAttribute("aria-label")) {
			this.selectElement.setAttribute("aria-label", "Filtrer");
		}

		// Set initial value to "all" if not already set
		if (!this.selectElement.value) {
			this.selectElement.value = "all";
		}

		// Add change event listener
		this.selectElement.addEventListener(
			"change",
			this.boundHandlers.selectChange,
		);
	}

	/**
	 * Handle select element change event
	 */
	handleSelectChange(e) {
		const selectedValue = e.target.value;
		this.filterByTag(selectedValue);
	}

	/**
	 * Filter items by tag/service
	 * @param {string} tag - The tag(s) to filter by ('all' shows all items)
	 *                        Can be a single tag or comma-separated tags
	 */
	filterByTag(tag) {
		if (tag === this.activeFilter) return;

		this.activeFilter = tag || "all";
		// Parse filter tags and cache them
		this.activeFilterTags = this.parseFilterTags(this.activeFilter);

		// Dispatch event for search filter to coordinate
		this.container.dispatchEvent(
			new CustomEvent("tagFilter:filterChanged", {
				detail: { filter: this.activeFilter },
			}),
		);

		// Use requestAnimationFrame for smooth transitions
		requestAnimationFrame(() => {
			this.performFilter();
		});
	}

	/**
	 * Perform the actual filtering operation
	 */
	performFilter() {
		let visibleCount = 0;
		const hiddenItems = [];
		let firstVisibleItem = null;
		const isAllFilter = this.activeFilter === "all";
		const filterTags = this.activeFilterTags || [];

		// Consistent selector for focusable elements
		const focusableSelector =
			"a, button, input, select, textarea, [tabindex]:not([tabindex='-1'])";

		// Batch DOM operations for better performance
		this.items.forEach((item) => {
			// Skip if item is no longer in DOM (defensive check)
			if (!item.isConnected) {
				return;
			}

			// Check if item is hidden by search filter (from filter-search.js)
			const isHiddenBySearch = item.dataset.hiddenBySearch === "true";

			// Check current visibility state to avoid unnecessary DOM operations
			const isCurrentlyHidden = item.classList.contains("is-hidden");

			// Check if item has data-tag attribute
			const hasTagAttr = item.hasAttribute("data-tag");

			let shouldShow;
			if (isHiddenBySearch) {
				// Items hidden by search should remain hidden
				shouldShow = false;
			} else if (isAllFilter) {
				// Show all items when "all" is selected (unless hidden by search)
				shouldShow = true;
			} else if (hasTagAttr) {
				// Use data-tag for all items
				const itemTags = this.getItemTags(item);
				// Check if item has any of the filter tags (OR logic)
				shouldShow = filterTags.some((filterTag) =>
					itemTags.includes(filterTag),
				);
			} else {
				// Items without data-tag should be hidden when filtering
				shouldShow = false;
			}

			// Only update DOM if visibility state actually changed
			if (shouldShow) {
				if (isCurrentlyHidden) {
					item.classList.remove("is-hidden");
					item.setAttribute("aria-hidden", "false");
					// Restore accessibility for focusable elements
					const inaccessibleElements = item.querySelectorAll("[tabindex='-1']");
					inaccessibleElements.forEach((el) => {
						if (el.matches("a, button, input, select, textarea")) {
							el.removeAttribute("tabindex");
						} else {
							el.setAttribute("tabindex", "0");
						}
					});
				}
				// Track first visible item for focus management
				if (!firstVisibleItem) {
					firstVisibleItem = item;
				}
				visibleCount++;
			} else {
				if (!isCurrentlyHidden) {
					item.classList.add("is-hidden");
					item.setAttribute("aria-hidden", "true");
					// Make focusable elements within inaccessible
					const focusableElements = item.querySelectorAll(focusableSelector);
					focusableElements.forEach((el) => {
						const currentTabIndex = el.getAttribute("tabindex");
						if (!currentTabIndex || currentTabIndex === "0") {
							el.setAttribute("tabindex", "-1");
						}
					});
				}
				hiddenItems.push(item);
			}
		});

		this.visibleCount = visibleCount;

		// Show/hide no results message (for office lists)
		if (this.noResultsMessage) {
			this.updateNoResultsMessage(visibleCount);
		}

		// Announce results to screen readers
		this.announceResults(visibleCount);

		// Manage focus if active element becomes hidden
		this.manageFocus(hiddenItems, firstVisibleItem);
	}

	/**
	 * Parse filter tags from a string (supports comma-separated values)
	 * @param {string} filterValue - The filter value to parse
	 * @returns {Array<string>} Array of normalized tag strings
	 */
	parseFilterTags(filterValue) {
		if (!filterValue || filterValue === "all") {
			return [];
		}

		// Support both space-separated and comma-separated tags
		// Normalize by converting to lowercase
		return filterValue
			.split(this.tagSplitRegex)
			.map((tag) => tag.trim().toLowerCase())
			.filter((tag) => tag.length > 0);
	}

	/**
	 * Get tags from an item element (with caching for performance)
	 * @param {HTMLElement} item - The item element
	 * @returns {Array<string>} Array of tag strings
	 */
	getItemTags(item) {
		if (!item || !(item instanceof HTMLElement)) {
			return [];
		}

		// Return cached tags if available
		if (this.itemTagsCache.has(item)) {
			return this.itemTagsCache.get(item);
		}

		const tagAttr = item.getAttribute("data-tag");
		if (!tagAttr) {
			this.itemTagsCache.set(item, []);
			return [];
		}

		// Support both space-separated and comma-separated tags
		const tags = tagAttr
			.split(this.tagSplitRegex)
			.map((tag) => tag.trim().toLowerCase())
			.filter((tag) => tag.length > 0);

		// Cache the result (with size limit to prevent unbounded growth)
		// If cache gets too large, clear oldest entries (simple FIFO)
		if (this.itemTagsCache.size > 1000) {
			const firstKey = this.itemTagsCache.keys().next().value;
			this.itemTagsCache.delete(firstKey);
		}
		this.itemTagsCache.set(item, tags);
		return tags;
	}

	/**
	 * Initialize items with proper attributes and cache tags
	 */
	initializeItems() {
		this.items.forEach((item) => {
			item.setAttribute("aria-hidden", "false");
			// Pre-cache tags for better filtering performance
			if (item.hasAttribute("data-tag")) {
				this.getItemTags(item);
			}
		});
	}

	/**
	 * Create live region for screen reader announcements
	 */
	createLiveRegion() {
		let liveRegion = this.container.querySelector(
			".tag-filter__live-region, .service-filter__live-region",
		);
		if (!liveRegion) {
			liveRegion = document.createElement("div");
			liveRegion.className = "visually-hidden tag-filter__live-region";
			liveRegion.setAttribute("role", "status");
			liveRegion.setAttribute("aria-live", "polite");
			liveRegion.setAttribute("aria-atomic", "true");
			this.container.appendChild(liveRegion);
		}
		this.liveRegion = liveRegion;
	}

	/**
	 * Create visible "no results" message element (for office lists)
	 */
	createNoResultsMessage() {
		let noResultsMessage = this.container.querySelector(
			".service-filter__no-results",
		);
		if (!noResultsMessage) {
			noResultsMessage = document.createElement("div");
			noResultsMessage.className = "service-filter__no-results";
			noResultsMessage.setAttribute("aria-live", "polite");
			noResultsMessage.setAttribute("aria-atomic", "true");
			// Insert before items container
			if (this.itemsContainer) {
				this.itemsContainer.parentNode.insertBefore(
					noResultsMessage,
					this.itemsContainer,
				);
			} else {
				this.container.appendChild(noResultsMessage);
			}
		}
		this.noResultsMessage = noResultsMessage;
		// Initially hidden
		noResultsMessage.style.display = "none";
	}

	/**
	 * Update the visible "no results" message
	 * @param {number} count - Number of visible items
	 */
	updateNoResultsMessage(count) {
		if (!this.noResultsMessage) return;

		// Only show message when filter is active (not "all") and no results
		if (count === 0 && this.activeFilter !== "all") {
			// Build message with location name
			const locationText = this.locationName ? `i ${this.locationName} ` : "";
			const message = `Ingen av kontorene ${locationText}tilbyr denne tjenesten.`;

			this.noResultsMessage.textContent = message;
			this.noResultsMessage.style.display = "block";
		} else {
			this.noResultsMessage.style.display = "none";
		}
	}

	/**
	 * Announce filter results to screen readers
	 * @param {number} count - Number of visible items
	 */
	announceResults(count) {
		if (!this.liveRegion) return;

		let message;
		if (count === 0 && this.activeFilter !== "all") {
			message = "Ingen resultater funnet.";
		} else {
			message = `${count} ${count === 1 ? "element" : "elementer"} vises.`;
		}

		// Clear and set new message
		// Use requestAnimationFrame for better timing with DOM updates
		requestAnimationFrame(() => {
			this.liveRegion.textContent = "";
			requestAnimationFrame(() => {
				this.liveRegion.textContent = message;
			});
		});
	}

	/**
	 * Manage focus when active element becomes hidden
	 * @param {Array<HTMLElement>} hiddenItems - Items that were hidden
	 * @param {HTMLElement|null} firstVisibleItem - First visible item (pre-computed for performance)
	 */
	manageFocus(hiddenItems, firstVisibleItem = null) {
		const activeElement = document.activeElement;
		const isActiveElementHidden = hiddenItems.some(
			(item) => item === activeElement || item.contains(activeElement),
		);

		// Only manage focus if the active element was hidden
		if (isActiveElementHidden) {
			// Use pre-computed firstVisibleItem if provided, otherwise find it
			if (!firstVisibleItem) {
				firstVisibleItem = this.items.find(
					(item) => !item.classList.contains("is-hidden"),
				);
			}

			if (firstVisibleItem) {
				// Focus on first focusable element within the visible item
				const firstFocusable = firstVisibleItem.querySelector(
					"a, button, [tabindex='0']",
				);
				if (firstFocusable) {
					firstFocusable.focus();
				} else {
					firstVisibleItem.focus();
				}
			} else if (this.selectElement) {
				// Focus on select element if no items visible
				this.selectElement.focus();
			}
		}
	}
}

/**
 * Initialize all tag filters on the page
 */
function initTagFilters() {
	const containers = document.querySelectorAll(".js-tag-filter");
	containers.forEach((container) => {
		// Prevent duplicate initialization
		if (!container.dataset.tagFilterInitialized) {
			container.dataset.tagFilterInitialized = "true";
			new TagFilter(container);
		}
	});
}

// Initialize when DOM is ready
// Use a small delay to ensure all content is rendered
let initTimeout;
let isInitialized = false;

function scheduleInit() {
	if (initTimeout || isInitialized) return; // Prevent duplicate scheduling
	initTimeout = setTimeout(() => {
		initTagFilters();
		initTimeout = null;
		isInitialized = true;
	}, 100);
}

// Only add listener if DOM is still loading
if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", scheduleInit);
} else {
	// DOM is already ready, initialize immediately
	scheduleInit();
}
